<template>
  <div style="display: none;">
    <!-- 此组件不需要UI，仅用于后台检测 -->
  </div>
</template>

<script>
import { session } from '@/libs/location'

export default {
  name: 'CheckUpdates',
  data() {
    return {
      checkInterval: null,
      checkIntervalTime: 2 * 60 * 1000, // 默认2分钟检查一次
      isChecking: false,
      hasShownUpdateDialog: false // 防止重复弹窗
    }
  },
  mounted() {
    this.initCheck()
  },
  beforeDestroy() {
    this.clearCheckInterval()
  },
  methods: {
    /**
     * 初始化检查
     */
    initCheck() {
      // 首次加载时，保存当前资源的etag和last-modified
      this.saveCurrentResourceInfo()

      // 设置定时检查
      this.checkInterval = setInterval(() => {
        this.checkResourceUpdate()
      }, this.checkIntervalTime)
    },

    /**
     * 保存当前资源信息
     */
    async saveCurrentResourceInfo() {
      try {
        const resourceInfo = await this.fetchResourceInfo()
        if (resourceInfo) {
          session.setItem('RESOURCE_ETAG', resourceInfo.etag || '')
          session.setItem('RESOURCE_LAST_MODIFIED', resourceInfo.lastModified || '')
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('保存资源信息失败:', error)
        }
      }
    },

    /**
     * 获取资源路径
     */
    getResourcePath() {
      // 判断是否部署在子路径下
      const currentPath = window.location.pathname
      console.log('当前路径:', currentPath)

      // 检查当前路径是否以 /ssoportal 开头（根据项目实际情况调整）
      if (currentPath.startsWith('/ssoportal/') || currentPath === '/ssoportal') {
        // 应用部署在 /ssoportal 子路径下
        return '/ssoportal/index.html'
      } else {
        // 可能是本地开发环境或部署在根目录
        return '/index.html'
      }
    },

    /**
     * 获取资源信息（etag和last-modified）
     */
    async fetchResourceInfo() {
      const resourcePath = this.getResourcePath()
      const url = window.location.origin + resourcePath

      try {
        // 使用HEAD请求获取index.html的响应头
        const response = await fetch(url, {
          method: 'HEAD',
          cache: 'no-cache'
        })

        if (response.ok) {
          const etag = response.headers.get('ETag') || response.headers.get('etag')
          const lastModified = response.headers.get('Last-Modified') || response.headers.get('last-modified')

          return {
            etag: etag || '',
            lastModified: lastModified || ''
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('获取资源信息失败:', error)
        }
        // 如果HEAD请求失败，尝试使用GET请求
        try {
          const response = await fetch(url, {
            method: 'GET',
            cache: 'no-cache'
          })

          if (response.ok) {
            const etag = response.headers.get('ETag') || response.headers.get('etag')
            const lastModified = response.headers.get('Last-Modified') || response.headers.get('last-modified')

            return {
              etag: etag || '',
              lastModified: lastModified || ''
            }
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('备用获取资源信息也失败:', e)
          }
        }
      }
      return null
    },

    /**
     * 检查资源是否更新
     */
    async checkResourceUpdate() {
      // 防止重复检查
      if (this.isChecking || this.hasShownUpdateDialog) {
        return
      }

      this.isChecking = true

      try {
        const currentResourceInfo = await this.fetchResourceInfo()

        if (!currentResourceInfo) {
          this.isChecking = false
          return
        }

        // 获取本地存储的资源信息
        const savedEtag = session.getItem('RESOURCE_ETAG') || ''
        const savedLastModified = session.getItem('RESOURCE_LAST_MODIFIED') || ''

        // 检查是否有更新
        const isUpdated = this.isResourceUpdated(
          savedEtag,
          savedLastModified,
          currentResourceInfo.etag,
          currentResourceInfo.lastModified
        )

        if (isUpdated) {
          this.showUpdateDialog()
        } else {
          // 资源未更新，更新本地存储的信息（以防服务器时间变化等）
          session.setItem('RESOURCE_ETAG', currentResourceInfo.etag || '')
          session.setItem('RESOURCE_LAST_MODIFIED', currentResourceInfo.lastModified || '')
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('检查资源更新失败:', error)
        }
      } finally {
        this.isChecking = false
      }
    },

    /**
     * 判断资源是否已更新
     */
    isResourceUpdated(savedEtag, savedLastModified, currentEtag, currentLastModified) {
      // 优先使用ETag判断
      if (currentEtag && savedEtag) {
        return currentEtag !== savedEtag
      }

      // 如果没有ETag，使用Last-Modified判断
      if (currentLastModified && savedLastModified) {
        const currentTime = new Date(currentLastModified).getTime()
        const savedTime = new Date(savedLastModified).getTime()
        return currentTime !== savedTime
      }

      // 如果都没有，但当前有值而保存的没有，说明可能是首次检查，不算更新
      if ((currentEtag || currentLastModified) && !savedEtag && !savedLastModified) {
        return false
      }

      // 其他情况，默认认为未更新
      return false
    },

    /**
     * 显示更新提示对话框
     */
    showUpdateDialog() {
      if (this.hasShownUpdateDialog) {
        return
      }

      this.hasShownUpdateDialog = true

      this.$Modal.warning({
        title: this.$t('system.updateTitle') || '系统更新提示',
        content: this.$t('system.updateMessage') || '检测到系统已更新，请点击确定刷新页面获取最新版本。',
        okText: this.$t('qd') || '确定',
        closable: false,
        maskClosable: false,
        onOk: () => {
          // 用户确认，刷新页面
          this.refreshPage()
        }
      })
    },

    /**
     * 刷新页面
     */
    refreshPage() {
      // 更新本地存储的资源信息
      this.saveCurrentResourceInfo().then(() => {
        // 强制刷新页面，清除缓存
        window.location.reload(true)
      })
    },

    /**
     * 清除检查定时器
     */
    clearCheckInterval() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval)
        this.checkInterval = null
      }
    }
  }
}
</script>

<style scoped>
/* 此组件不需要样式 */
</style>